import { sql } from 'drizzle-orm';
import { jsonb, timestamp, uuid, varchar } from 'drizzle-orm/pg-core';

export const id = () => uuid('id').primaryKey().defaultRandom();

export const timestamps = {
  createdAt: timestamp('created_at', { withTimezone: true }).notNull().default(sql`now()`),
  updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().default(sql`now()`),
};

/**
 * The memory-mesh SUBSTRATE (additive, 2026-06-25, Wave 1). Spread like `timestamps`:
 *   { id: id(), ...myCols, ...substrate, ...timestamps }
 * Bitemporal supersede-never-delete (valid/recorded windows + the `supersedesId` chain) +
 * the `sourceRef` NO-INVENTION provenance gate + the (subject, predicate, object) `triple`
 * the future intelligence layer projects over.
 * NOTE: `sourceRef` is NULLABLE here — the NOT NULL write-gate lands in Wave 2, so no live
 * insert breaks while the write paths are still unaware of it.
 */
export const substrate = {
  validFrom: timestamp('valid_from', { withTimezone: true }).default(sql`now()`),
  validTo: timestamp('valid_to', { withTimezone: true }),
  recordedAt: timestamp('recorded_at', { withTimezone: true }).default(sql`now()`),
  supersededAt: timestamp('superseded_at', { withTimezone: true }),
  supersedesId: uuid('supersedes_id'),
  sourceRef: jsonb('source_ref'),
  triple: jsonb('triple'),
};

/** A single provenance source behind a lake/pond row. */
export interface LakeSource {
  url: string;
  title?: string;
  retrievedAt?: string; // ISO, stamped at research time
}

/**
 * Lake-provenance columns — the staleness/verification banding every pond shares
 * (mirrors platform_algorithm_rules / au_visa_data). Spread like `timestamps`:
 *   { id: id(), ...myCols, ...lakeProvenance, ...timestamps }
 * verifyState: 'seed' | 'inherited' | 'calibrated' | 'researched' | 'stale'.
 */
export const lakeProvenance = {
  verifyState: varchar('verify_state', { length: 16 }).notNull().default('seed'),
  sources: jsonb('sources').$type<LakeSource[]>().default(sql`'[]'::jsonb`),
  lastVerifiedAt: timestamp('last_verified_at', { withTimezone: true }),
};
